/**
 * Your own position in the token on screen.
 *
 * Every other table on this page is about other people. This one is the only
 * row that is yours, and a token page without it makes you open the portfolio
 * in another tab to answer the first question you have: am I already in this,
 * and at what.
 *
 * The shaping is here rather than in the component because the interesting
 * parts are arithmetic with holes in it — a position that is fully sold has no
 * current size but still has a realised result, and one never opened is not
 * the same as one closed at zero.
 */

/**
 * Plain numbers, read off the model's atoms by the component.
 *
 * The position in the registry is a reatom model whose every field is an atom,
 * so the unwrapping belongs where the hooks are; what is left here is the
 * arithmetic, which is the part worth a test.
 */
export type RawPosition = {
  remainingUsd: number
  remainingTokens: number
  boughtUsd: number
  buys: number
  soldUsd: number
  sells: number
  entryPriceUsd: number
  entryMarketCap: number
  realisedUsd: number
  unrealisedUsd: number
  closedAt: Date | undefined
}

export type PositionReading = {
  /** What it is worth now; zero once it has all been sold. */
  sizeUsd: number
  sizeTokens: number
  boughtUsd: number
  soldUsd: number
  buys: number
  sells: number
  entryPriceUsd: number
  entryMarketCap: number
  realisedUsd: number
  unrealisedUsd: number
  pnlUsd: number
  /** Against what was put in, which is the only denominator that means much. */
  pnlPercent: number
  /** Still holding, as against sold out or never opened. */
  open: boolean
}

const sum = (left: number, right: number): number =>
  (Number.isFinite(left) ? left : 0) + (Number.isFinite(right) ? right : 0)

export const positionOf = (
  position: RawPosition | undefined,
): PositionReading | undefined => {
  if (!position) {
    return undefined
  }

  const pnlUsd = sum(position.realisedUsd, position.unrealisedUsd)

  return {
    sizeUsd: position.remainingUsd,
    sizeTokens: position.remainingTokens,
    boughtUsd: position.boughtUsd,
    soldUsd: position.soldUsd,
    buys: position.buys,
    sells: position.sells,
    entryPriceUsd: position.entryPriceUsd,
    entryMarketCap: position.entryMarketCap,
    realisedUsd: position.realisedUsd,
    unrealisedUsd: position.unrealisedUsd,
    pnlUsd,
    pnlPercent:
      Number.isFinite(position.boughtUsd) && position.boughtUsd > 0
        ? (pnlUsd / position.boughtUsd) * 100
        : Number.NaN,
    /* Closed is the server's word for it; a dust balance left behind after a
       sell-all would otherwise read as still being in. */
    open: position.closedAt === undefined && position.remainingTokens > 0,
  }
}

/**
 * What to say when there is no position.
 *
 * Three different nothings, and only one of them is worth a sentence: no
 * wallet chosen is a thing to fix, never having bought is ordinary, and having
 * sold out is a result.
 */
export const emptyReason = ({ hasWallet }: { hasWallet: boolean }): string =>
  hasWallet
    ? 'You have not traded this token.'
    : 'Choose a wallet to see your position here.'
